// Helpers puros do fluxo de download. Sem state local, sem acoplamento com
// Vuex, sem side-effects — apenas transformações e classificações. Mantém
// download.js focado em orquestração (queue + mutations + actions).

// Formata bytes em MB/GB com 1 casa pra mensagem de toast — mostrar "1287324567 B"
// não ajuda ninguém. GB > 1024 MB; MB > 1 MB; abaixo disso é insignificante e
// reportamos como "<1 MB" pra evitar "0.0 MB".
export function formatBytes (n) {
  if (!n || n < 0) return '0 MB';
  const MB = 1024 * 1024;
  const GB = 1024 * MB;
  if (n >= GB) return `${(n / GB).toFixed(1)} GB`;
  if (n >= MB) return `${Math.round(n / MB)} MB`;
  return '<1 MB';
}

// Token/customerId vêm de `enroll.webcastIntegration`. Se algum dos dois estiver
// faltando, qualquer fetch contra o CoreMedia/Wowza vai voltar 401 — preflight
// silenciaria o erro e contabilizaria a aula como 0 bytes (turma inteira passa
// no check mas falha aula por aula). Bloquear cedo é mais honesto: avisar o
// user e não enfileirar nada.
export function hasWebcastCredentials (webcast) {
  return !!(webcast && webcast.accessToken && webcast.applicationId);
}

// Detecta se a falha do batch foi por rede vs erro real (HTTP 4xx/5xx, disco,
// formato inválido). Heurística em camadas, do mais confiável pro mais frágil:
//   1. dom.isConnected === false: app sabe que tá offline → certamente rede.
//   2. failedItems em cascata (>= 3 items no batch error): cascata típica de
//      timeouts em rede caindo. Falha pontual (URL inválida, 404 específico)
//      raramente derruba 3+ items.
//   3. Keywords no message: backup pra casos onde a rede oscilou rápido entre
//      offline/online e isConnected já voltou true quando o catch rodou.
//
// `isConnected === false` (explícito) em vez de `!isConnected` pra evitar que
// undefined ou null sejam interpretados como offline.
export function isNetworkError (err, isConnected) {
  if (isConnected === false) return true;
  const msg = (err && (err.message || String(err))) || '';
  // Padrão atual do batchFailed: "Batch lo_X falhou: Items failed [seg_5,seg_6,...]"
  // Conta items entre colchetes; cascata = provavelmente rede.
  const bracketMatch = msg.match(/\[([^\]]*)\]/);
  if (bracketMatch) {
    const items = bracketMatch[1].split(',').filter(s => s.trim().length > 0);
    if (items.length >= 3) return true;
  }
  // HTTP 5xx é tratado como transitório (502/503/504 são tipicamente
  // balancer/CDN momentaneamente fora) — preserva o partial e tenta de novo
  // em vez de apagar tudo via cleanup pós-falha. Sem isso, uma janela curta
  // de Cloudflare 521 destruía downloads completos em vídeos e PDFs.
  // `wifi-only` é o sinal do bloqueio nativo quando a rede vira cellular num
  // download wifi-only — tratado como pausa (preserva parciais), não falha real.
  // `UnknownHost|resolve host|ENOTFOUND|ERR_NAME`: falha de DNS com isConnected
  // true — típico de captive portal que sequestra o DNS (rede aberta) em vez de
  // responder 200 com HTML. Sem isso, o CapacitorHttp lança
  // "UnknownHostException: Unable to resolve host ..." e a mensagem não casava
  // com nada aqui → falha real → cleanup apagava os parciais.
  return /network|timeout|unreachable|connect|IOException|NSURLError|ECONN|offline|wifi-only|UnknownHost|resolve host|ENOTFOUND|ERR_NAME|HTTP 5\d\d/i.test(msg);
}

// Decide se um LO deve ser SKIPADO no fluxo de download — espelha a lógica
// de `_canDownloadLO` no ComposedHeader (que controla a visibilidade do ícone),
// mas no caminho de execução do store pra cobrir cliques em turma/módulo e
// proteger contra qualquer chamada programática.
//
// Skip = não baixa, não marca downloaded, deixa idle. Os 2 casos:
//   1. LO exclusivamente online (forms/external sem broadcast/pdf/html) —
//      tecnicamente impossível baixar (precisa backend em runtime).
//   2. PDF cujo produtor desabilitou download via DRM
//      (`desabilitarDownloadVisualizador`) — respeita a restrição mesmo em
//      download de turma inteira.
export function shouldSkipDownload (lo, drm, courseId) {
  if (!lo) return true;
  const isExternalVideo = !!(lo.broadcast && lo.broadcast.isExternal);
  // `lo.audio` faz parte do baixável (o downloadLearningObject tem branch pra
  // ele). Faltava aqui, então um LO só-áudio combinado com forms/externalContent
  // era classificado como "exclusivamente online" e PULADO no download de turma —
  // mas continuava contando no denominador de getClassDownloadedRatio, travando o
  // anel da turma abaixo de 100% pra sempre. _canDownloadLO já o incluía.
  const hasDownloadable = !!((lo.broadcast && !isExternalVideo) || lo.pdf || lo.htmlContent || lo.audio);
  const isOnlineOnly = !hasDownloadable && !!(lo.forms || lo.externalContent || isExternalVideo);
  if (isOnlineOnly) return true;
  if (lo.pdf && drm && drm.codCurso == courseId && drm.desabilitarDownloadVisualizador) {
    return true;
  }
  return false;
}

export function hasAnyDownloads (downloadsState, enrollId) {
  const enroll = downloadsState[enrollId];
  if (!enroll) return false;
  const hasLO = Object.values(enroll.learningObjects || {}).some(s => s !== 'idle');
  const hasClass = Object.values(enroll.classes || {}).some(s => s !== 'idle');
  return hasLO || hasClass;
}

export function findLO (grid, loId) {
  const searchClasses = (classes) => {
    for (const cls of (classes || [])) {
      const lo = (cls.learningObjects || []).find(lo => String(lo.id) === String(loId));
      if (lo) return lo;
    }
    return null;
  };

  if (!grid) return null;

  let found = searchClasses(grid.classes);
  if (found) return found;

  for (const mod of (grid.modules || [])) {
    found = searchClasses(mod.classes);
    if (found) return found;
    for (const disc of (mod.disciplines || [])) {
      found = searchClasses(disc.classes);
      if (found) return found;
    }
  }

  for (const disc of (grid.disciplines || [])) {
    found = searchClasses(disc.classes);
    if (found) return found;
  }

  return null;
}

// Normaliza os 4 formatos possíveis de grid pra UI de "Meus Downloads":
//   - grid.modules[].classes[].learningObjects[]
//   - grid.modules[].disciplines[].classes[].learningObjects[]
//   - grid.disciplines[].classes[].learningObjects[]   (sem módulos)
//   - grid.classes[].learningObjects[]                 (curso plano)
//
// Devolve hierarquia Curso → (Módulo OU Classe) → LearningObject:
//   { groups: [{ kind, id, name, orderIndex, learningObjects: [...] }] }
//
// `kind` indica a unidade do agrupador (`module` | `discipline` | `class`)
// pra UI rotular adequadamente. Quando o curso tem módulos, cada módulo é um
// group e os LOs de todas as classes filhas aparecem juntos dentro dele
// (compatível com a UX existente do app que trata "aula" como LO). Quando o
// curso é plano (só classes), cada class vira um group próprio.
// Containers do grid (module/discipline/class) usam `.title` no backend
// EAD; learningObject usa `.name` (ou `.title` como fallback). Sem o fallback
// `title || name` os headers de módulo/turma ficavam vazios e a UI escondia
// o agrupamento, jogando todas as aulas num balaio só sob o curso.
const titleOf = (o) => (o && (o.title || o.name)) || '';

export function flattenGridForDownloads (grid) {
  if (!grid) return { groups: [] };

  const losFromClasses = (classes) => {
    const out = [];
    (classes || []).forEach((cls, classIdx) => {
      (cls.learningObjects || []).forEach((lo, loIdx) => {
        out.push({
          id: lo.id,
          name: lo.name || lo.title || titleOf(cls),
          classId: cls.id,
          className: titleOf(cls),
          duration: (lo.broadcast && lo.broadcast.duration) || lo.seconds || lo.duration || 0,
          thumbnail: lo.image || cls.image || null,
          orderIndex: classIdx * 1000 + loIdx
        });
      });
    });
    return out;
  };

  if (Array.isArray(grid.modules) && grid.modules.length) {
    return {
      groups: grid.modules.map((mod, idx) => {
        const direct = losFromClasses(mod.classes);
        const fromDisciplines = (mod.disciplines || [])
          .reduce((acc, disc) => acc.concat(losFromClasses(disc.classes)), []);
        return {
          kind: 'module',
          id: mod.id,
          name: titleOf(mod),
          orderIndex: idx,
          learningObjects: direct.concat(fromDisciplines)
        };
      })
    };
  }

  if (Array.isArray(grid.disciplines) && grid.disciplines.length) {
    return {
      groups: grid.disciplines.map((disc, idx) => ({
        kind: 'discipline',
        id: disc.id,
        name: titleOf(disc),
        orderIndex: idx,
        learningObjects: losFromClasses(disc.classes)
      }))
    };
  }

  if (Array.isArray(grid.classes) && grid.classes.length) {
    return {
      groups: grid.classes.map((cls, idx) => ({
        kind: 'class',
        id: cls.id,
        name: titleOf(cls),
        orderIndex: idx,
        learningObjects: (cls.learningObjects || []).map((lo, loIdx) => ({
          id: lo.id,
          name: lo.name || lo.title || titleOf(cls),
          classId: cls.id,
          className: titleOf(cls),
          duration: (lo.broadcast && lo.broadcast.duration) || lo.seconds || lo.duration || 0,
          thumbnail: lo.image || cls.image || null,
          orderIndex: loIdx
        }))
      }))
    };
  }

  return { groups: [] };
}

export function collectClassesFromGrid (grid) {
  if (!grid) return [];
  const out = [];
  (grid.classes || []).forEach(c => out.push(c));
  (grid.modules || []).forEach(mod => {
    (mod.classes || []).forEach(c => out.push(c));
    (mod.disciplines || []).forEach(d => (d.classes || []).forEach(c => out.push(c)));
  });
  (grid.disciplines || []).forEach(d => (d.classes || []).forEach(c => out.push(c)));
  return out;
}

export function collectImageUrls (fullGrid, enrolls) {
  const urls = new Set();

  const addImg = (obj) => {
    if (obj && obj.image) urls.add(obj.image);
    if (obj && obj.logo) urls.add(obj.logo);
  };

  const collectClasses = (classes) => {
    (classes || []).forEach(cls => {
      addImg(cls);
      (cls.learningObjects || []).forEach(lo => addImg(lo));
    });
  };

  addImg(fullGrid);
  collectClasses(fullGrid.classes);
  (fullGrid.modules || []).forEach(mod => {
    addImg(mod);
    collectClasses(mod.classes);
    (mod.disciplines || []).forEach(disc => collectClasses(disc.classes));
  });
  (fullGrid.disciplines || []).forEach(disc => collectClasses(disc.classes));

  (enrolls || []).forEach(e => addImg(e));

  return [...urls].filter(Boolean);
}
